import { Checkbox, Flex, Form, Input, InputNumber, Select } from 'antd';
import { BaseButton } from 'components/button/button-base';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import { FormUnsavedChangesWrapper as CustomForm } from 'components/form/Form';
import { SelectWithOther } from 'components/form/SelectWithOther';
import { Subtitle, Title } from 'components/mvp-typography';
import { styles } from 'features/sign-in/v2/styles';
import { useOrganisationRegistrationMutation } from 'lib/hooks/tanstack/useOrganisationRegistrationMutation';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { FormQuestion } from 'lib/types/form';
import { useErrorList } from 'lib/utils/useErrorList';
import { useEffect } from 'react';

interface OrganisationRegistrationFormPageProps {
  goToNextStep: (organisationId: string) => void;
  organisationId: string;
}

const businessDetailsQuestions: FormQuestion[] = [
  {
    name: 'name',
    label: 'Name of Business',
    component: () => <Input placeholder="Name of Business" />,
    rules: [{ required: true, message: 'Please input your business name.' }]
  },
  {
    name: 'country',
    label: 'Country',
    component: () => (
      <Select
        options={[
          {
            value: 'CA',
            label: 'CA (Canada)'
          },
          { value: 'US', label: 'US (United States of America)' }
        ]}
      />
    ),
    rules: [{ required: true }]
  },
  {
    name: 'city',
    label: 'City',
    component: () => <Input placeholder="City" />,
    rules: [{ required: true, message: 'Please input your city.' }]
  },
  {
    name: 'address',
    label: 'Address',
    component: () => <Input placeholder="Address" />,
    rules: [
      { required: true, message: 'Please input your address.' }
    ]
  },
  {
    name: 'addressLine2',
    label: 'Address Line 2',
    component: () => <Input placeholder="Address Line 2" />
  },
  {
    name: 'organisationCategoryAndFocus',
    label: 'Business category',
    component: () => (
      <SelectWithOther
        options={[
          {
            value: 'Clinical Practice',
            label: 'Clinical Practice'
          },
          { value: '_School', label: 'School' },
          { value: 'Athletic Club', label: 'Athletic Club' },
          { value: 'other', label: 'Other' }
        ]}
      />
    ),
    rules: [{ required: true, message: 'Please select your business category and focus.' }]
  },
];

const contactQuestions: FormQuestion[] = [
  {
    name: 'contactEmail',
    label: 'Email',
    component: () => <Input placeholder="Email" />,
    rules: [
      { required: true, message: 'Please input your email.' },
      { type: 'email', message: 'Please enter a valid email.' }
    ]
  },
  {
    name: 'phoneNumber',
    label: 'Phone Number',
    component: () => <Input placeholder="Phone Number" />,
    rules: [
      {
        required: true,
        message: 'Please input your phone number.'
      }
    ]
  },
];

const additionalDetailsQuestions: FormQuestion[] = [
  {
    name: ['organisationMetadata', 'userCount'],
    label: 'Estimated number of users (organisational staff)',
    tooltip: 'Number of people administering the organisation.',
    component: () => (
      <InputNumber
        min={1}
        style={{ width: '100%' }}
        placeholder="Estimated number of users"
      />
    ),
    rules: [
      {
        required: true,
        message: 'Please input estimated number of users.'
      }
    ]
  },
  {
    name: ['organisationMetadata', 'estimatedTestTakerCount'],
    label:
      'Estimated number of test-takers per year (e.g. athletes, students, patients)',
    tooltip: 'Number of people taking tests.',
    component: () => (
      <InputNumber
        min={0}
        style={{ width: '100%' }}
        placeholder="Estimated number of test-takers per year"
      />
    ),
    rules: [
      {
        required: true,
        message: 'Please input estimated number of test-takers.'
      }
    ]
  }
];

interface OrganisationFormFields {
  name: string;
  address: string;
  addressLine2: string | undefined;
  phoneNumber: string;
  country: 'CA' | 'US';
  contactEmail: string;
  organisationCategoryAndFocus: string;
  organisationMetadata: {
    userCount: number;
    estimatedTestTakerCount: number;
  };
  city: string;
  __skipFormField_agreement: boolean;
}

export default function OrganisationRegistrationFormPage(
  props: OrganisationRegistrationFormPageProps
) {
  const [form] = Form.useForm<OrganisationFormFields>();
  const errorList = useErrorList();

  const organisationRegistrationMutation = useOrganisationRegistrationMutation({
    onSuccessCallback: newOrganisationId => {
      props.goToNextStep(newOrganisationId);
    }
  });

  useQueryErrorNotifier(
    { error: organisationRegistrationMutation.error },
    errorList
  );

  useEffect(() => {
    if (errorList.hasErrors) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errorList.hasErrors]);

  async function handleSubmit(formValues: OrganisationFormFields) {
    errorList.reset();

    const customerType = formValues.organisationCategoryAndFocus === 'Clinical Practice' ? 'clinic' : 'standard';

    organisationRegistrationMutation.mutate({
      name: formValues.name,
      address: formValues.address,
      addressLine2: formValues.addressLine2 ?? null,
      contactEmail: formValues.contactEmail,
      phoneNumber: formValues.phoneNumber,
      organisationMetadata: {
        ...formValues.organisationMetadata,
        businessType: formValues.organisationCategoryAndFocus
      },
      country: formValues.country,
      provinceOrState: null,
      billing: {
        address: null,
        primaryContact: {
          email: null,
          name: null
        }
      },
      city: formValues.city,
      faxNumber: null,
      postalOrZipCode: null,
      clinicId: props.organisationId,
      customerType: customerType
    });
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '20px',
        gap: 16
      }}
    >
      {errorList.hasErrors && <ErrorListDisplay errorList={errorList} />}
      <Title>Create your Organization</Title>
      <CustomForm>
        <Form
          form={form}
          style={styles.form}
          layout="vertical"
          name="organisation_registration_form"
          onFinish={handleSubmit}
        >
          <Flex vertical gap={24}>
            <Flex vertical gap={16}>
              <Subtitle>Organization Details</Subtitle>
              {businessDetailsQuestions.map(question => (
                <Form.Item
                  key={
                    Array.isArray(question.name)
                      ? question.name.join('.')
                      : question.name
                  }
                  name={question.name}
                  label={question.label}
                  rules={question.rules}
                  extra={question.extra}
                  tooltip={question.tooltip}
                >
                  {question.component()}
                </Form.Item>
              ))}
            </Flex>
            <Flex vertical gap={16}>
              <Subtitle>Organization Contact Information</Subtitle>
              {contactQuestions.map(question => (
                <Form.Item
                  key={
                    Array.isArray(question.name)
                      ? question.name.join('.')
                      : question.name
                  }
                  name={question.name}
                  label={question.label}
                  rules={question.rules}
                  extra={question.extra}
                  tooltip={question.tooltip}
                >
                  {question.component()}
                </Form.Item>
              ))}
            </Flex>
            <Flex vertical gap={16}>
              <Subtitle>Additional Details</Subtitle>
              {additionalDetailsQuestions.map(question => (
                <Form.Item
                  key={
                    Array.isArray(question.name)
                      ? question.name.join('.')
                      : question.name
                  }
                  name={question.name}
                  label={question.label}
                  rules={question.rules}
                  extra={question.extra}
                  tooltip={question.tooltip}
                >
                  {question.component()}
                </Form.Item>
              ))}
            </Flex>
            <Form.Item
              name="__skipFormField_agreement"
              valuePropName="checked"
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    value
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(
                            'Please confirm the information is correct.'
                          )
                        )
                }
              ]}
            >
              <Checkbox>
                By clicking this checkbox, you are confirming that this
                information is correct and you are an authorized representative
                of the organization.
              </Checkbox>
            </Form.Item>

            <div style={{ marginTop: '20px' }}>
              <BaseButton
                type="primary"
                htmlType="submit"
                loading={organisationRegistrationMutation.isLoading}
                disabled={organisationRegistrationMutation.isLoading}
              >
                Next
              </BaseButton>
            </div>
          </Flex>
        </Form>
      </CustomForm>
    </div>
  );
}
